import {
    DialogContent, DialogTitle, Dialog, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Switch,
    TextField, DialogActions, IconButton, Typography, Box
} from "@mui/material";
import React, {createElement, KeyboardEventHandler} from "react";
import { ChangeEventHandler, useContext, useState, FunctionComponent } from "react";
import { Check, Close } from "@mui/icons-material";
import { connect } from "react-redux";
import { selectConstants, selectTags } from "../../redux/selectors/firebase";
import { FirebaseContext } from "../firebase/FirebaseProvider";
import { Constants, Tags } from "../graph/StateTags";
import { ConfirmContext } from "../../utility/ConfirmProvider";
import { constantHasConstraint, getConstantSelectValues, selectionHasUniqueTag, updateConstraintData } from "../../utility/constantsTagsUtils";
import { selectProject } from "../../redux/selectors/editor";
import {DatePicker, LocalizationProvider, TimePicker} from "@mui/lab";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import enLocale from "date-fns/locale/en-GB";

interface ITagValueProps
{
    onValueChange: (value: any) => void
}
interface IStringTagValueProps extends ITagValueProps
{
    value: string
    handleKeyPress: (event: React.KeyboardEvent<HTMLElement>) => void
}
function StringTagValue({ onValueChange, value, handleKeyPress }: IStringTagValueProps)
{
    const handleChange: ChangeEventHandler<HTMLInputElement> = ({ target }) =>
        onValueChange(target.value);

    return (
        <TextField autoFocus fullWidth label="Value" defaultValue={value} size="small" onChange={handleChange}
                   onKeyPress={handleKeyPress} />
    );
}
interface INumberTagValueProps extends ITagValueProps
{
    value: number
    handleKeyPress: (event: React.KeyboardEvent<HTMLElement>) => void
}
function NumberTagValue({ onValueChange, value, handleKeyPress }: INumberTagValueProps)
{
    const handleChange: ChangeEventHandler<HTMLInputElement> = ({ target }) =>
        onValueChange(target.value);

    return (
        <TextField autoFocus fullWidth label="Value" defaultValue={value} size="small" onChange={handleChange}
                   onKeyPress={handleKeyPress} type="number" />
    );
}
interface IBooleanTagValueProps extends ITagValueProps
{
    value: boolean
    handleKeyPress: (event: React.KeyboardEvent<HTMLElement>) => void
}
function BooleanTagValue({ onValueChange, value, handleKeyPress }: IBooleanTagValueProps)
{
    const handleValueChange: ChangeEventHandler<HTMLInputElement> = ({ target }) =>
        onValueChange(target.checked);

    return (
        <Switch autoFocus checked={Boolean(value)} onChange={handleValueChange} onKeyPress={handleKeyPress} />
    );
}
interface IDateTagValueProps extends ITagValueProps
{
    value: number
    handleKeyPress: (event: React.KeyboardEvent<HTMLElement>) => void
}
function DateTagValue({onValueChange, value}: IDateTagValueProps) {
    const date = new Date(value);

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} locale={enLocale}>
            <DatePicker label="Date" value={date} onChange={onValueChange} renderInput={(params) =>
                <TextField size="small" {...params}/>
            }/>
            <TimePicker onChange={onValueChange} value={date} renderInput={(params) =>
                <TextField size="small" {...params}/>
            }/>
        </LocalizationProvider>
    );
}
interface ISelectTagValueProps extends IStringTagValueProps
{
    values: { [key: string]: string }
    handleKeyPress: (event: React.KeyboardEvent<HTMLElement>) => void
}
function SelectTagValue({ onValueChange, value, values, handleKeyPress }: ISelectTagValueProps)
{
    const handleValueChange = ({ target }: SelectChangeEvent) =>
    {
        onValueChange(target.value);
    }

    return (
        <FormControl fullWidth sx={{marginTop:1}}>
            <InputLabel>Tag Value</InputLabel>
            <Select autoFocus fullWidth label="Tag Type" value={value} size="small" onChange={handleValueChange} onKeyPress={handleKeyPress}>
                {Object.keys(values).map(itemValue =>
                    <MenuItem key={itemValue} value={itemValue}>{values[itemValue]}</MenuItem>
                )}
            </Select>
        </FormControl>
    );
}

interface Props
{
    project: string
    selection: string[]
    type: string
    onClose: () => void
    constants: Constants,
    tags: Tags
}

const tagValueComponents: { [type: string]: FunctionComponent<any> } = {
    boolean: BooleanTagValue,
    date: DateTagValue,
    default: StringTagValue,
    number: NumberTagValue,
    select: SelectTagValue
}

function AddTagDialog({ project, selection, type, onClose, constants, tags }: Props)
{
    const { push, set } = useContext(FirebaseContext);
    const confirm = useContext(ConfirmContext);

    const constant = constants[type];

    const constraintIsUnique = constantHasConstraint(constant, "unique");
    const selectionHasUniqueTagEntry = constraintIsUnique && selectionHasUniqueTag(selection, tags, type);

    const addTag = (state: string, type: string, value: any) =>
    {
        value = value.getTime ? value.getTime() : value;
        push(`tags/${project}/${state}`, { type, value })
            .then(() => updateConstraintData(type, value, constants, project, set));
    }

    const updateTag = (id: string, type: string, state: string, value: any) =>
    {
        value = value.getTime ? value.getTime() : value;
        set(`tags/${project}/${state}/${id}/value`, value)
            .then(()=> updateConstraintData(type, value, constants, project, set));
    }

    const updateTagsForSelection = () =>
    {
        selection.forEach((state) =>
        {
            const tagEntry = Object.entries(tags[state] ?? {}).find(([_id, tag]) => tag.type === type && (constraintIsUnique || tag.value === value));

            if (tagEntry)
            {
                const [id, tag] = tagEntry;
                if (value !== tag.value) {
                    localStorage.setItem("addTagDialog.lastType", type);
                    localStorage.setItem("addTagDialog.lastValue", value.toString());
                    updateTag(id, type, state, value);
                }
            }
            else
            {
                addTag(state, type, value);
                localStorage.setItem("addTagDialog.lastType", type);
                localStorage.setItem("addTagDialog.lastValue", value.toString());
            }
        })
        onClose();
    }

    const getTagValue = (state: string, type: string) =>
    {
        return Object.values(tags[state] ?? {}).find((tag) => tag.type === type)?.value;
    }

    const values: { [key: string]: string } = getConstantSelectValues(constant);
    const valueType = Object.keys(values).length > 0 ? "select" : constant.type;

    const defaultValues: { [type: string]: string | number | boolean } = {
        default: "",
        select: Object.keys(values)[0] ?? "",
        boolean: false,
        number: 0,
    }
    const getDefaultValue = (type: string): string | boolean | number => {
        if (type === "date") {
            return Date.now();
        } else if (type === "boolean") {
            return true;
        } else if (type === "number") {
            return 0;
        }  else if (type === "string") {
            return "";
        } else {
            const constant = constants[localStorage.getItem("addTagDialog.lastType") ?? ""];

            if (constant) {
                if (constant.type === "string" || constant.type === "select")
                    return localStorage.getItem("addTagDialog.lastValue") ?? "";
                else if (constant.type === "boolean")
                    return Boolean(localStorage.getItem("addTagDialog.lastValue"));
                else
                    return Number(localStorage.getItem("addTagDialog.lastValue"));
            } else {
                return defaultValues[type] ?? defaultValues.default;
            }
        }
    }
    const [value, setValue] = useState<string | boolean | number>(getDefaultValue(valueType));

    const disabledConfirm: { [type: string]: () => boolean } = {
        default: () => value === "",
        select: () => !((value as string) in values),
        boolean: () => false,
        number: () => false
    }

    const addDisabled = (disabledConfirm[valueType] ?? disabledConfirm.default)();

    const getDisplayValue = (value: string) =>
    {
        return String(value) in values ? values[value as string] : value;
    }

    const updateTags = () =>
    {
        if (selectionHasUniqueTagEntry)
        {
            const displayValue = String(value) in values ? values[value as string] : value;
            const confirmContent = selection.length === 1 ?
                <>Do you wish to change this unique tag <br /> {constant.name}: {getDisplayValue(String(getTagValue(selection[0], type)))} to {String(displayValue)}?</> :
                <>Nodes in this selection already have this unique tag set for {constant.name}. <br /> Do you wish to replace them with {constant.name}: {displayValue}?</>
            confirm(<Typography>{confirmContent}</Typography>).then(updateTagsForSelection).catch(onClose);
        }
        else
        {
            updateTagsForSelection();
        }
    }

    const handleClickAdd = () =>
    {
        updateTags();
    }
    const handleDialogKeyUp: KeyboardEventHandler<HTMLElement> = event => event.key === "Enter" && updateTags();

    return (
        <Dialog open={true} onClose={onClose}>
            <DialogTitle>
                Add {constant.name} tag to {selection.length === 1 ? selection[0] : `${selection.length} nodes`}
            </DialogTitle>
            <DialogContent>
                <Box p={1} onKeyUp={handleDialogKeyUp}>
                    {createElement(tagValueComponents[valueType] ?? tagValueComponents.default, {
                        onValueChange: setValue,
                        value,
                        values
                    })}
                </Box>
            </DialogContent>
            <DialogActions>
                <IconButton onClick={handleClickAdd} disabled={addDisabled}>
                    <Check />
                </IconButton>
                <IconButton onClick={onClose}>
                    <Close />
                </IconButton>
            </DialogActions>
        </Dialog >
    );
}

const mapStateToProps = (store: never) => ({
    constants: selectConstants(store) || {},
    tags: selectTags(store) || {},
    project: selectProject(store)
});

export default connect(mapStateToProps, {
})(AddTagDialog);

